import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Seo from '../components/Seo';
import { useAuth } from '../context/AuthContext';
import { listTools } from '../services/tools';

const ArrowIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M5 12h14M12 5l7 7-7 7"/>
  </svg>
);

/**
 * Tool artwork, keyed by the backend's `tool_id`.
 *
 * Icons stay client-side deliberately: the API returns an emoji (e.g. "🏦"),
 * which does not match this layout's line-art style or respect the theme's
 * stroke colour. Everything else — name, description, price, paid/free,
 * coming-soon — comes from the API.
 *
 * An unknown tool_id falls back to a generic mark rather than rendering nothing,
 * so a newly seeded tool still appears without a frontend release.
 */
const TOOL_ICONS = {
  sipcalculator: (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="4" y="2" width="16" height="20" rx="2"/><line x1="8" y1="6" x2="16" y2="6"/>
      <line x1="8" y1="10" x2="8" y2="10"/><line x1="12" y1="10" x2="12" y2="10"/><line x1="16" y1="10" x2="16" y2="10"/>
      <line x1="8" y1="14" x2="8" y2="14"/><line x1="12" y1="14" x2="12" y2="14"/><line x1="16" y1="14" x2="16" y2="14"/>
      <line x1="8" y1="18" x2="16" y2="18"/>
    </svg>
  ),
  riskrewardcalculator: (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/>
    </svg>
  ),
  papertrading: (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M22 7 13.5 15.5 8.5 10.5 2 17"/><path d="M16 7h6v6"/>
    </svg>
  ),
};

const FALLBACK_ICON = (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="9"/><path d="M12 8v4l3 2"/>
  </svg>
);

/** Route each tool opens once signed in. */
const TOOL_ROUTES = {
  // Must match the route in App.js. It is /tools/papertrading, not
  // /papertrading — the latter has no route and falls through to the SPA
  // catch-all, rendering an empty page.
  papertrading: '/tools/papertrading',
};

function formatPrice(tool) {
  if (!tool.is_paid) return 'Free';
  const amount = Number(tool.price);
  if (!Number.isFinite(amount)) return null;
  const symbol = tool.currency === 'INR' ? '\u20b9' : '';
  return `${symbol}${amount.toLocaleString('en-IN')}`;
}

const Tools = () => {
  const { isAuthenticated, loading: authLoading } = useAuth();
  const [tools, setTools] = useState([]);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    listTools()
      .then((res) => {
        if (cancelled) return;
        const rows = res.data?.data || [];
        // Only active tools are public. is_coming_soon is kept and rendered as a
        // badge — those are deliberately advertised before launch.
        setTools(rows.filter((t) => t.is_active));
      })
      .catch(() => { if (!cancelled) setFailed(true); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  return (
    <>
      <Seo
        title="Tools — InvFly"
        description="Free stock market tools including SIP calculator and risk-reward calculator for Indian market participants."
      />

      <div className="ph">
        <div className="container">
          <div className="ph-tag">Resources</div>
          <div className="ph-title">Tools</div>
          <p className="ph-desc">Free tools to support structured thinking and disciplined market participation.</p>
        </div>
      </div>

      <section className="sec">
        <div className="container">
          {loading && <p className="ph-desc">Loading tools…</p>}

          {!loading && failed && (
            <p className="ph-desc">
              Tools are temporarily unavailable. Please refresh in a moment.
            </p>
          )}

          {!loading && !failed && tools.length === 0 && (
            <p className="ph-desc">No tools are available right now.</p>
          )}

          <div className="tools-grid">
            {tools.map((t) => {
              const isLive = !t.is_coming_soon;
              const price = formatPrice(t);
              const route = TOOL_ROUTES[t.tool_id];
              return (
                <div key={t.tool_id || t._id} className="tool-card">
                  <div className="tool-icon">{TOOL_ICONS[t.tool_id] || FALLBACK_ICON}</div>
                  <div>
                    <div className="tool-name">{t.name}</div>
                    <div style={{ marginTop: '4px', display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
                      {isLive
                        ? <span className="badge-live">Live</span>
                        : <span className="badge-soon">Coming Soon</span>}
                      {t.is_paid && <span className="badge-soon">Premium</span>}
                    </div>
                  </div>
                  {t.tagline && <div className="tool-desc" style={{ opacity: 0.75 }}>{t.tagline}</div>}
                  <div className="tool-desc">{t.description}</div>

                  {/* Price comes from the API so an admin edit is reflected here.
                      It was previously hardcoded, which is how the page ended up
                      advertising a price the backend disagreed with. */}
                  {price && (
                    <div style={{ fontWeight: 700, fontSize: 15 }}>
                      {price}{t.is_paid ? <span style={{ fontWeight: 400, fontSize: 12, opacity: 0.7 }}> / month</span> : null}
                    </div>
                  )}

                  {isLive && !authLoading && (
                    isAuthenticated
                      ? (route
                          ? <Link to={route} className="btn-p" style={{ alignSelf: 'flex-start', padding: '8px 14px', fontSize: '12px' }}>
                              Open Tool <ArrowIcon />
                            </Link>
                          : <button className="btn-p" style={{ alignSelf: 'flex-start', padding: '8px 14px', fontSize: '12px' }}>
                              Open Tool <ArrowIcon />
                            </button>)
                      : (
                        <Link to="/signin" className="btn-enr" style={{ alignSelf: 'flex-start', padding: '8px 14px', fontSize: '12px' }}>
                          Sign In
                        </Link>
                      )
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <div className="cta-sec">
        <div className="container">
          <div className="cta-title">Build Your <span className="txt-ac">Market Toolkit</span></div>
          <p className="cta-desc">Combine our tools with structured courses to develop a disciplined approach to markets.</p>
          <div className="cta-acts">
            <Link to="/courses" className="btn-p">Explore Courses <ArrowIcon /></Link>
            <Link to="/contact" className="btn-s">Contact Us</Link>
          </div>
        </div>
      </div>
    </>
  );
};

export default Tools;
